import type { EnhancedAbsenceModel } from '@reducers/orm/absence/absence.model';
import type { EmployeeTeamDepartments } from '@reducers/orm/employee/employee.selectors';
import type { ExchangeWithScheduleModel } from '@reducers/orm/exchange/exchange.model';
import type { TreeItem } from '@sb/ui/tree';

export interface DepartmentOption {
  id: string;
  name: string;
  locationName: string;
  /** "Location - Department" */
  label: string;
}

interface SectionItem {
  id?: string;
}

export interface DepartmentGroup<T extends SectionItem> {
  departmentId: string;
  name: string;
  locationName: string;
  label: string;
  items: T[];
  /** Per item id, the other departments the same item is listed under. */
  alsoIn: Record<string, string>;
}

export interface DepartmentSections<T extends SectionItem> {
  /** Groups for the selected departments, in department selector order. */
  groups: DepartmentGroup<T>[];
  /** Groups for the other departments, holding only the items no selected group lists. */
  otherGroups: DepartmentGroup<T>[];
  /** Items without a known department; shown last, whatever the selection. */
  noDepartmentItems: T[];
  /** Items with a known department, none of them selected. */
  hiddenCount: number;
}

/**
 * The departments the department filter offers, labelled "Location - Department", in its order.
 * Takes the filter's own location → department tree so both always list the same departments.
 */
export const toDepartmentOptions = (locationTree: TreeItem<string>[]): DepartmentOption[] =>
  locationTree.flatMap((location) =>
    (location.children ?? []).map((department) => ({
      id: department.value,
      name: department.text,
      locationName: location.text,
      label: `${location.text} - ${department.text}`,
    })),
  );

/**
 * Lists each item, in the given order, under every selected department it belongs to.
 * Departments that are unknown or deleted count as no department.
 */
export const toDepartmentSections = <T extends SectionItem>(
  items: T[],
  departmentIdsOf: (item: T) => (string | null | undefined)[],
  departmentOptions: DepartmentOption[],
  selectedDepartmentIds: string[],
): DepartmentSections<T> => {
  const labelById = new Map(departmentOptions.map((option) => [option.id, option.label]));
  const knownDepartmentIds = new Map(
    items.map((item) => [
      item,
      [
        ...new Set(
          departmentIdsOf(item).filter(
            (departmentId): departmentId is string => !!departmentId && labelById.has(departmentId),
          ),
        ),
      ],
    ]),
  );
  const knownDepartmentIdsOf = (item: T): string[] => knownDepartmentIds.get(item) ?? [];
  const selected = new Set(selectedDepartmentIds);

  const hiddenItems = items.filter((item) => {
    const departmentIds = knownDepartmentIdsOf(item);

    return departmentIds.length > 0 && !departmentIds.some((departmentId) => selected.has(departmentId));
  });

  const toGroups = (options: DepartmentOption[], groupableItems: T[]): DepartmentGroup<T>[] =>
    options
      .map(({ id: departmentId, name, locationName, label }) => {
        const groupItems = groupableItems.filter((item) => knownDepartmentIdsOf(item).includes(departmentId));
        const alsoIn = groupItems.flatMap((item) => {
          const otherLabels = knownDepartmentIdsOf(item)
            .filter((otherId) => otherId !== departmentId)
            .flatMap((otherId) => labelById.get(otherId) ?? []);

          return item.id && otherLabels.length > 0 ? [[item.id, otherLabels.join(', ')]] : [];
        });

        return { departmentId, name, locationName, label, items: groupItems, alsoIn: Object.fromEntries(alsoIn) };
      })
      .filter((group) => group.items.length > 0);

  return {
    groups: toGroups(
      departmentOptions.filter((option) => selected.has(option.id)),
      items,
    ),
    otherGroups: toGroups(
      departmentOptions.filter((option) => !selected.has(option.id)),
      hiddenItems,
    ),
    noDepartmentItems: items.filter((item) => knownDepartmentIdsOf(item).length === 0),
    hiddenCount: hiddenItems.length,
  };
};

type GroupableAbsence = Pick<EnhancedAbsenceModel, 'id' | 'user_id' | 'startdate'>;

type GroupableExchange = Pick<ExchangeWithScheduleModel, 'id' | 'date'> & {
  Schedule: Pick<ExchangeWithScheduleModel['Schedule'], 'starttime'> & {
    Team?: Pick<NonNullable<ExchangeWithScheduleModel['Schedule']['Team']>, 'department_id'>;
  };
};

const byStartDate = (firstAbsence: GroupableAbsence, secondAbsence: GroupableAbsence) =>
  firstAbsence.startdate.localeCompare(secondAbsence.startdate);

const byShiftStart = (firstExchange: GroupableExchange, secondExchange: GroupableExchange) =>
  `${firstExchange.date} ${firstExchange.Schedule.starttime}`.localeCompare(
    `${secondExchange.date} ${secondExchange.Schedule.starttime}`,
  );

/** Absences by start date, each under every department its owner belongs to. */
export const toAbsenceDepartmentSections = <T extends GroupableAbsence>(
  absences: T[],
  departmentsByEmployee: EmployeeTeamDepartments,
  departmentOptions: DepartmentOption[],
  selectedDepartmentIds: string[],
): DepartmentSections<T> =>
  toDepartmentSections(
    absences.toSorted(byStartDate),
    (absence) => departmentsByEmployee[absence.user_id] ?? [],
    departmentOptions,
    selectedDepartmentIds,
  );

/**
 * Pending absences and exchanges together: each department lists its absences by start date,
 * then its exchanges by shift start. An exchange belongs to the department of its shift's team,
 * the one its approval permission is checked against.
 */
export const toPendingDepartmentSections = <TAbsence extends GroupableAbsence, TExchange extends GroupableExchange>(
  absences: TAbsence[],
  departmentsByEmployee: EmployeeTeamDepartments,
  exchanges: TExchange[],
  departmentOptions: DepartmentOption[],
  selectedDepartmentIds: string[],
): DepartmentSections<TAbsence | TExchange> => {
  const sortedAbsences = absences.toSorted(byStartDate);
  const sortedExchanges = exchanges.toSorted(byShiftStart);
  const departmentIds = new Map<TAbsence | TExchange, (string | null | undefined)[]>();

  for (const absence of sortedAbsences) {
    departmentIds.set(absence, departmentsByEmployee[absence.user_id] ?? []);
  }

  for (const exchange of sortedExchanges) {
    departmentIds.set(exchange, [exchange.Schedule.Team?.department_id]);
  }

  return toDepartmentSections<TAbsence | TExchange>(
    [...sortedAbsences, ...sortedExchanges],
    (item) => departmentIds.get(item) ?? [],
    departmentOptions,
    selectedDepartmentIds,
  );
};

/** How many distinct items the sections list, counting one listed in several groups once. */
export const countSectionItems = ({ groups, noDepartmentItems }: DepartmentSections<SectionItem>): number =>
  new Set([...groups.flatMap((group) => group.items), ...noDepartmentItems]).size;
